import { type ReactNode, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Mark } from "../../lib/Mark.tsx";
import { type Period, periodOf } from "./Sky.tsx";

/** A look around Atzmai OS's home screen, drawn at the screen's own size
 *  (1600 by 900) and scaled to fit: its clock, date, greeting and sky are the
 *  visitor's own, and the dock opens each app as the OS does, full screen.
 *  Nothing here does real work; the apps are pictures of the real ones. */

type App = "chat" | "web" | "docs" | "files" | "terminal" | "apps" | "settings" | "tasks";

const DOCK: { app: App; label: string; tile: string; icon: ReactNode }[] = [
  { app: "chat", label: "Atzmai", tile: "linear-gradient(160deg,#b9c3ff,#7f8cf0)", icon: <Mark size={34} /> },
  { app: "web", label: "The web", tile: "linear-gradient(160deg,#6fe0f5,#2aa9d8)", icon: <Glyph d="M12 3a9 9 0 100 18 9 9 0 000-18zm0 0c2.5 2.4 3.8 5.4 3.8 9s-1.3 6.6-3.8 9m0-18C9.5 5.4 8.2 8.4 8.2 12s1.3 6.6 3.8 9M3.5 9h17M3.5 15h17" /> },
  { app: "docs", label: "Documents", tile: "linear-gradient(160deg,#6aa2ff,#3c6ef0)", icon: <Glyph d="M7 3h7l5 5v13H7zM14 3v5h5M10 13h6M10 17h6" /> },
  { app: "files", label: "Files", tile: "linear-gradient(160deg,#ffd166,#f5a524)", icon: <Glyph d="M3 7a2 2 0 012-2h4l2 2h8a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z" fill /> },
  { app: "terminal", label: "Terminal", tile: "linear-gradient(160deg,#2b2f55,#191b38)", icon: <Glyph d="M5 8l4 4-4 4M12 17h7" /> },
  { app: "apps", label: "Apps", tile: "linear-gradient(160deg,#c49bff,#8e5cf0)", icon: <Glyph d="M5 5h5v5H5zM14 5h5v5h-5zM5 14h5v5H5zM14 14h5v5h-5z" fill /> },
  { app: "settings", label: "Settings", tile: "linear-gradient(160deg,#7ff0c2,#2fbf8a)", icon: <Glyph d="M12 9a3 3 0 100 6 3 3 0 000-6zm7.4 3a7.4 7.4 0 00-.1-1.2l2-1.6-2-3.4-2.4 1a7.6 7.6 0 00-2-1.2L14.5 3h-5l-.4 2.6a7.6 7.6 0 00-2 1.2l-2.4-1-2 3.4 2 1.6a7.4 7.4 0 000 2.4l-2 1.6 2 3.4 2.4-1a7.6 7.6 0 002 1.2l.4 2.6h5l.4-2.6a7.6 7.6 0 002-1.2l2.4 1 2-3.4-2-1.6c.1-.4.1-.8.1-1.2z" dark /> },
];

const SHOTS: Partial<Record<App, { src: string; alt: string }>> = {
  chat: { src: "/screens/website.webp", alt: "Atzmai's chat making a website, with the page shown beside it" },
  docs: { src: "/screens/letter.webp", alt: "A letter Atzmai wrote, open beside the chat, with Open in its program" },
  tasks: { src: "/screens/tasks.webp", alt: "Tasks: a plan Atzmai made from a sentence, and what's coming up" },
};

function greeting(h: number) {
  return h >= 5 && h < 12 ? "Good morning" : h >= 12 && h < 17 ? "Good afternoon" : "Good evening";
}

function useNow() {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 10_000);
    return () => clearInterval(t);
  }, []);
  return now;
}

export function TryAtzmai() {
  const outer = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(0);
  const [open, setOpen] = useState<App | null>(null);
  const [folder, setFolder] = useState("Documents");
  const [big, setBig] = useState(false);
  const [flash, setFlash] = useState(false);
  const [asked, setAsked] = useState("");
  const now = useNow();
  const period: Period = periodOf(now.getHours());

  useLayoutEffect(() => {
    const el = outer.current;
    if (!el) return;
    const fit = () => setScale(el.clientWidth / 1600);
    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    if (!open) return;
    const esc = (e: KeyboardEvent) => e.key === "Escape" && setOpen(null);
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, [open]);

  const bar = now.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" }).replace(",", "");
  const time = now.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
  const date = now.toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" }).replace(",", "");

  function screenshot() {
    setFlash(true);
    setTimeout(() => setFlash(false), 900);
  }

  return (
    <div ref={outer} className="os-frame" style={{ aspectRatio: "16 / 9" }}>
      <div className="os-screen" data-t={period} data-big={big || undefined} style={{ transform: `scale(${scale})` }} inert={scale === 0 || undefined}>
        <div className="os-bar">
          <span className="flex items-center gap-3"><Mark size={30} /> <b>Atzmai</b></span>
          <span className="flex items-center gap-5">
            <Glyph d="M2 9a15 15 0 0120 0M5.5 12.5a10 10 0 0113 0M9 16a5 5 0 016 0M12 19.5h.01" size={22} stroke />
            <Glyph d="M4 9v6h4l5 4V5L8 9zM16 9a4 4 0 010 6M18.5 6.5a8 8 0 010 11" size={22} stroke />
            <b>{bar}</b><b>{time}</b>
          </span>
        </div>

        <div className="os-home" aria-hidden={open ? true : undefined}>
          <span className="os-planet"><Mark size={150} /></span>
          <h3 className="os-hello">{greeting(now.getHours())}</h3>
          <p className="os-date">{date}</p>
          <form className="os-ask" onSubmit={(e) => { e.preventDefault(); setOpen("chat"); }}>
            <input aria-label="Ask, or open anything" placeholder="Ask, or open anything" value={asked} onChange={(e) => setAsked(e.target.value)} />
            <button aria-label="Ask"><Glyph d="M4 12l16-8-6 16-2-6z" size={24} fill dark /></button>
          </form>
          <div className="os-chips">
            <button onClick={() => { setFolder("Pictures"); setOpen("files"); }}>Show my pictures</button>
            <button onClick={screenshot}>Take a screenshot</button>
            <button onClick={() => setBig((b) => !b)}>{big ? "Make the text smaller" : "Make the text bigger"}</button>
          </div>
        </div>

        <div className="os-dock" role="toolbar" aria-label="Dock">
          {DOCK.map((d) => (
            <button key={d.app} aria-label={`Open ${d.label}`} title={d.label} style={{ background: d.tile }} className={open === d.app ? "os-open" : ""}
              onClick={() => { if (d.app === "files") setFolder("Documents"); setOpen(d.app); }}>
              {d.icon}
            </button>
          ))}
        </div>

        {open && (
          <div className="os-app" role="region" aria-label={DOCK.find((d) => d.app === open)?.label ?? "Tasks"}>
            {SHOTS[open] ? <img src={SHOTS[open]!.src} alt={SHOTS[open]!.alt} width={1600} height={900} /> : <Drawn app={open} folder={folder} setFolder={setFolder} openApp={setOpen} />}
            <button className="os-home-bar" onClick={() => setOpen(null)}>Back to home</button>
          </div>
        )}
        {flash && <div className="os-flash" role="status">Saved to Pictures</div>}
      </div>
    </div>
  );
}

/** The apps there is no picture of yet, drawn plainly. */
function Drawn({ app, folder, setFolder, openApp }: { app: App; folder: string; setFolder: (f: string) => void; openApp: (a: App) => void }) {
  if (app === "files") {
    const items: Record<string, [string, string][]> = {
      Documents: [["Letter to the landlord.docx", "#3c6ef0"], ["Budget.ods", "#2fbf8a"], ["CV.docx", "#3c6ef0"], ["Shop hours.pdf", "#e5566f"]],
      Pictures: [["Graduation.jpg", "linear-gradient(135deg,#ffb07c,#e66f88)"], ["Lake Victoria.jpg", "linear-gradient(135deg,#3f8ee0,#8fcdf2)"], ["Market day.jpg", "linear-gradient(135deg,#ffd166,#f5a524)"], ["Screenshot.png", "linear-gradient(135deg,#6e33ad,#e66f88)"]],
      Downloads: [["atzmai-os.iso", "#8e5cf0"]],
      Music: [],
    };
    return (
      <div className="os-window os-files">
        <nav>{Object.keys(items).map((f) => <button key={f} className={f === folder ? "on" : ""} onClick={() => setFolder(f)}>{f}</button>)}</nav>
        <section>
          <h4>{folder}</h4>
          {items[folder]!.length ? (
            <div className="grid">
              {items[folder]!.map(([name, colour]) => (
                <div key={name} className="item"><span className={folder === "Pictures" ? "thumb photo" : "thumb"} style={{ background: colour }} />{name}</div>
              ))}
            </div>
          ) : <p className="empty">Nothing here yet. Ask Atzmai to find some music.</p>}
        </section>
      </div>
    );
  }
  if (app === "web") {
    return (
      <div className="os-window os-web">
        <div className="address"><Glyph d="M11 4a7 7 0 100 14 7 7 0 000-14zM20 20l-4-4" size={22} stroke />Search, or type a website</div>
        <div className="tiles">
          {["News", "Videos", "Maps", "School", "Weather", "Mail"].map((t, i) => (
            <div key={t}><span style={{ background: `hsl(${200 + i * 28} 70% 62%)` }}>{t[0]}</span>{t}</div>
          ))}
        </div>
        <p>Children see a safe web: the sites a parent allows, and nothing that isn't for them.</p>
      </div>
    );
  }
  if (app === "terminal") {
    return (
      <div className="os-window os-terminal">
        <p>Atzmai OS 0.2, built on Debian 13</p>
        <p><span>parent@home</span>:~$ <i /></p>
      </div>
    );
  }
  if (app === "settings") {
    return (
      <div className="os-window os-settings">
        <nav>{["Wi-Fi", "Sound", "Display", "Family", "Updates", "Privacy"].map((s) => <span key={s} className={s === "Updates" ? "on" : ""}>{s}</span>)}</nav>
        <section>
          <h4>Updates</h4>
          <div className="row"><b>Atzmai OS is up to date</b><span>Updates install by themselves, with a snapshot first.</span></div>
          <div className="row"><b>Undo the last update</b><span>Go back to the snapshot taken before it.</span></div>
        </section>
      </div>
    );
  }
  const apps: [App, string][] = [["chat", "Atzmai"], ["tasks", "Tasks"], ["web", "The web"], ["docs", "Documents"], ["files", "Files"], ["settings", "Settings"]];
  return (
    <div className="os-window os-apps">
      <h4>Apps</h4>
      <div className="grid">
        {apps.map(([a, label]) => {
          const d = DOCK.find((x) => x.app === a);
          return (
            <button key={a} onClick={() => openApp(a)}>
              <span style={{ background: d?.tile ?? "linear-gradient(160deg,#ffb07c,#e66f88)" }}>{d?.icon ?? <Glyph d="M12 7v5l3 2M12 3a9 9 0 100 18 9 9 0 000-18z" />}</span>
              {label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function Glyph({ d, size = 30, fill = false, dark = false, stroke = false }: { d: string; size?: number; fill?: boolean; dark?: boolean; stroke?: boolean }) {
  const colour = dark ? "#0e1030" : "#fff";
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden="true">
      <path d={d} fill={fill && !stroke ? colour : "none"} stroke={fill && !stroke ? "none" : colour} strokeWidth={stroke ? 1.8 : 2} strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
